import {
  CheckBox,
  FormControl,
  FormGroup,
  FormRow,
  ISelectOption,
  Select,
  Size,
  ValidationErrorMessage,
  Validators,
  useValidator,
  Text,
  FormCol,
  useTestId
} from '@cmctechnology/phoenix-stockbroking-web-design';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createKeyValueTranslatedToOptionList } from '../../common/mappings';
import { IQuestionProps } from '../../models/pepQuestions';
import { MONTHS_DISPLAY_WITH_NUMBERS, generateYearsDisplayWithNumbers } from '../../constants/commonConstants';

export const PEPQuestion5: React.FC<IQuestionProps> = ({ selected, setSelected, setValidators, answers, setAnswers, ...rest }) => {
  const { t } = useTranslation();
  const { generateTestId } = useTestId(rest, `pepQ5`);

  const monthsSelectOptions = createKeyValueTranslatedToOptionList(MONTHS_DISPLAY_WITH_NUMBERS, t);
  const yearsSelectOptions = generateYearsDisplayWithNumbers();

  const q5OffenceValidator = useValidator<string>('', Validators.required(t('Required')));
  const q5MonthValidator = useValidator<ISelectOption | undefined>(undefined, Validators.required(t('Required')));
  const q5YearValidator = useValidator<ISelectOption | undefined>(undefined, Validators.required(t('Required')));

  useEffect(() => {
    if (selected) setValidators([q5OffenceValidator, q5MonthValidator, q5YearValidator]);
    else setValidators([]);

    setAnswers({
      ...answers,
      question5: {
        answered: selected ?? false,
        offence: q5OffenceValidator.value,
        month: q5MonthValidator.value?.value,
        year: q5YearValidator.value?.value
      }
    });
  }, [selected, q5OffenceValidator.value, q5MonthValidator.value, q5YearValidator.value]);

  return (
    <FormGroup>
      <Text>{t('Have you been convicted of a criminal offence either domestically or internationally?')}</Text>
      <FormGroup>
        <FormRow>
          <CheckBox
            label={t('Yes')}
            checked={selected}
            onChange={(e) => {
              setSelected(e.target.checked);
            }}
            disabled={selected}
            {...generateTestId(`yes`)}
          />
          <CheckBox
            label={t('No')}
            checked={selected === false}
            onChange={(e) => {
              setSelected(!e.target.checked);
            }}
            disabled={selected === false}
            {...generateTestId(`no`)}
          />
        </FormRow>
      </FormGroup>

      {selected && (
        <FormGroup>
          <Text>{t('Please provide details and date of the offence.')}</Text>
          <FormControl
            type='text'
            name='positionName'
            placeholder={t('Enter the offence')}
            value={q5OffenceValidator.value}
            onChange={(e) => {
              q5OffenceValidator.handleEvent(e.target.value);
            }}
            invalid={q5OffenceValidator.invalid}
            {...generateTestId(`offenceInput`)}
          />
          <ValidationErrorMessage size={Size.Medium} validator={q5OffenceValidator} />
          <FormRow>
            <FormCol percentWidth={50}>
              <FormGroup {...generateTestId(`monthInput`)} style={{ marginTop: '0' }}>
                <Select
                  value={q5MonthValidator.value}
                  options={monthsSelectOptions}
                  placeholder={t('Select month')}
                  onChange={(option: ISelectOption | null) => {
                    q5MonthValidator.handleEvent(option as ISelectOption);
                  }}
                  invalid={q5MonthValidator.invalid}
                  isClearable
                />
                <ValidationErrorMessage size={Size.Medium} validator={q5MonthValidator} />
              </FormGroup>
            </FormCol>
            <FormCol percentWidth={50}>
              <FormGroup {...generateTestId(`yearInput`)} style={{ marginTop: '0' }}>
                <Select
                  value={q5YearValidator.value}
                  options={yearsSelectOptions}
                  placeholder={t('Select year')}
                  onChange={(option: ISelectOption | null) => {
                    q5YearValidator.handleEvent(option as ISelectOption);
                  }}
                  invalid={q5YearValidator.invalid}
                  isClearable
                />
                <ValidationErrorMessage size={Size.Medium} validator={q5YearValidator} />{' '}
              </FormGroup>
            </FormCol>
          </FormRow>
        </FormGroup>
      )}
    </FormGroup>
  );
};
